import { Controller } from "@hotwired/stimulus";

const MILES_TO_METERS = 1609.344;
const KILOMETERS_TO_METERS = 1000;
const MIN_RADIUS = 10;
const MAX_RADIUS = 100;

export default class GoogleMapController extends Controller {
  static targets = ["myMap", "radius", "showradius", "address", "radiusUnit"];

  connect() {
    this.formPrefix = this.element.dataset.googleMapFormPrefix;

    if (this.hasMyMapTarget) {
      const initialPosition = {
        lat: Number.parseFloat(this.myMapTarget.dataset.lat) || 40.7128,
        lng: Number.parseFloat(this.myMapTarget.dataset.lng) || -74.006,
        city: this.myMapTarget.dataset.city || "",
        state: this.myMapTarget.dataset.state || "",
        country: this.myMapTarget.dataset.country || "",
        place: this.myMapTarget.dataset.place || ""
      };

      this.setFormFields(initialPosition);
      this.initMap(initialPosition.lat, initialPosition.lng);

      this.updateFormFields({
        lat: initialPosition.lat,
        lng: initialPosition.lng
      });

      this.updateRadiusDisplay();
    }
  }

  setFormFields(fields) {
    for (const [key, value] of Object.entries(fields)) {
      if (!this.formPrefix) return;

      const input = document.getElementById(`${this.formPrefix}_${key}`);

      if (input) {
        input.value = value || "";
      }
    }

    if (this.hasAddressTarget) {
      this.addressTarget.value = fields.place || "";
    }

    const otherController =
      this.application.getControllerForElementAndIdentifier(
        document.getElementById("new-audience-form"),
        "audience"
      );
    otherController?.setLocalLocationFormData(fields);
  }

  async initMap(lat, lng) {
    const position = { lat, lng };

    const { Map: GoogleMap } = await google.maps.importLibrary("maps");
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");

    this.map = new GoogleMap(this.myMapTarget, {
      center: position,
      zoom: 10,
      mapId: this.myMapTarget.dataset.key
    });

    this.marker = new AdvancedMarkerElement({
      map: this.map,
      position,
      title: ""
    });

    this.circle = new google.maps.Circle({
      map: this.map,
      center: position,
      radius: this.radiusInMeters(),
      strokeColor: "#1E90FF",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#1E90FF",
      fillOpacity: 0.35
    });

    this.addMapClickListener();
  }

  addMapClickListener() {
    this.map.addListener("click", (event) => {
      const clickedLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      this.updateMarkerPosition(clickedLocation);
      this.updateFormFields(clickedLocation);
    });
  }

  updateMarkerPosition(position) {
    this.marker.position = position;
    this.circle.setCenter(position);
    this.map.setCenter(position);
    this.updateFormFields(position);
  }

  async updateFormFields(position) {
    const geocoder = new google.maps.Geocoder();
    const location = await this.reverseGeocode(geocoder, position);

    const fields = {
      lat: position.lat,
      lng: position.lng,
      city: location.city,
      state: location.state,
      country: location.country,
      place: location.place
    };

    this.setFormFields(fields);
  }

  async reverseGeocode(geocoder, position) {
    return new Promise((resolve, reject) => {
      geocoder.geocode({ location: position }, (results, status) => {
        if (status === "OK" && results[0]) {
          const addressComponents = results[0].address_components;

          const city =
            addressComponents.find((component) =>
              component.types.includes("locality")
            )?.long_name || "";
          const country =
            addressComponents.find((component) =>
              component.types.includes("country")
            )?.long_name || "";
          const state =
            addressComponents.find((component) =>
              component.types.includes("administrative_area_level_1")
            )?.long_name || "";

          resolve({
            city: city,
            country: country,
            state: state,
            place: results[0].formatted_address
          });
        } else {
          reject(
            new Error(`Geocoding failed: ${status}`)
          );
        }
      });
    });
  }

  flyToLocation(lat, lng) {
    const newPosition = {
      lat: Number.parseFloat(lat),
      lng: Number.parseFloat(lng)
    };
    this.updateMarkerPosition(newPosition);
  }

  updateRadius(event) {
    let radius = Number.parseFloat(event.target.value);

    if (!Number.isFinite(radius)) return;

    radius = this.clampRadius(radius);

    event.target.value = radius;

    this.circle.setRadius(this.radiusInMeters(radius));
    this.updateRadiusDisplay();
  }

  toggleRadiusUnit(event) {
    const newUnit = event.target.checked
      ? "kilometers"
      : "miles";

    this.radiusUnitTarget.value = newUnit;
    this.updateRadiusDisplay();
    this.circle.setRadius(this.radiusInMeters());
  }

  radiusInMeters(
    radius = Number.parseFloat(this.radiusTarget.value)
  ) {
    if (!Number.isFinite(radius)) {
      return MIN_RADIUS * MILES_TO_METERS;
    }

    if (this.radiusUnitTarget.value === "kilometers") {
      return radius * KILOMETERS_TO_METERS;
    }

    return radius * MILES_TO_METERS;
  }

  clampRadius(radius) {
    return Math.min(
      MAX_RADIUS,
      Math.max(MIN_RADIUS, radius)
    );
  }

  updateRadiusDisplay() {
    const radius = Number.parseFloat(
      this.radiusTarget.value
    );

    if (!Number.isFinite(radius)) return;

    const unit =
      this.radiusUnitTarget.value === "kilometers"
        ? "KM"
        : "Miles";

    this.showradiusTarget.innerHTML = `${radius} ${unit}`;
  }

  showAlert(title, message, icon) {
    Swal.fire({
      customClass: {
        popup:
          "swal-popup alert-sm-bg align-items-center justify-content-center w-100",
        htmlContainer: "text-black-paint text-white"
      },
      animation: false,
      title: title,
      text: message,
      icon: icon,
      confirmButtonText: "OK"
    });
  }
}
